import { AgGridEvent, GridApi } from "@ag-grid-community/core";
import { AgGridReactProps } from "@ag-grid-community/react";
import { useCallback, useEffect } from "react";
import { DataTableContext } from "shared/components/table/data/DataTableContext";
import { DataTableProps } from "shared/components/table/data/DataTableProps";
import {
  LoadingOverlayComponent,
  NoRowsOverlayComponent,
  OverlayProps,
} from "shared/components/table/maybern/MaybernTableOverlay";
import { useMaybernTableUIContext } from "shared/components/table/maybern/MaybernTableUIContext";
import { tryGetAgGridApi } from "shared/components/table/maybern/MaybernTableUtils";

export const useDataTableOverlayAgProps = (
  { emptyStateProps, isLoading }: DataTableProps,
  { isEmpty }: DataTableContext,
): AgGridReactProps => {
  const { agGrid } = useMaybernTableUIContext();

  const overlayProps: OverlayProps = {
    emptyStateProps,
    isLoading,
    isEmpty,
  };

  const updateOverlay = useCallback(
    (api: GridApi) => {
      if (api.isDestroyed()) {
        return;
      }
      if (isLoading) {
        api.showLoadingOverlay();
      } else if (isEmpty) {
        api.showNoRowsOverlay();
      } else {
        api.hideOverlay();
      }
    },
    [isEmpty, isLoading],
  );

  const onRowOrColumnsEvent = useCallback(
    (event: AgGridEvent) => {
      updateOverlay(event.api);
    },
    [updateOverlay],
  );

  useEffect(() => {
    const api = tryGetAgGridApi(agGrid);
    if (api) {
      updateOverlay(api);
    }
  }, [agGrid, updateOverlay]);

  return {
    suppressLoadingOverlay: !isLoading,
    suppressNoRowsOverlay: isLoading || !isEmpty,
    loadingOverlayComponent: LoadingOverlayComponent,
    loadingOverlayComponentParams: overlayProps,
    noRowsOverlayComponent: NoRowsOverlayComponent,
    noRowsOverlayComponentParams: overlayProps,
    // ag-grid automatically hides the overlay when these events are triggered,
    // so we need to re-show it if isLoading is true.
    onRowDataUpdated: onRowOrColumnsEvent,
    onNewColumnsLoaded: onRowOrColumnsEvent,
  };
};
